import { useTranslation } from 'next-i18next'
import React from 'react'

import { useStore } from '../../functions/store'
import DashboardHome from './DashboardHome'
import DashboardProfile from './DashboardProfile'
import { StepSelector } from './StepSelector'
import Tag from './Tag'
import DashboardCards from './cards/DashboardCards'
import { DashboardCompetition } from './competition'
import DashboardFeedback from './feedback/DashboardFeedback'
import { DashboardFeedbackByProfile } from './feedback/DashboardFeedbackByProfile'
import DashboardTable from './table/DashboardTable'
import GroupSelect from './table/GroupSelect'

type DashboardProps = {
  step: string
  /** When step is feedback-by-profile, the target profile UUID for single-card evaluation (no shared group) */
  profileUserUuid?: string
}

function Dashboard({ step, profileUserUuid }: DashboardProps) {
  const user = useStore((state) => state)
  const { t } = useTranslation('avaliacao360')

  const handleNavigate = (newStep: string) => {
    user.setUser({ ...user, step: newStep })
  }

  const stepConfig = {
    home: {
      tag: (
        <Tag color="bg-slate-700" className="py-1.5 sm:py-2 whitespace-nowrap">
          {t('home.title', 'Início')}
        </Tag>
      ),
    },
    table: {
      tag: (
        <Tag color="bg-amber-700" className="py-1.5 sm:py-2 whitespace-nowrap">
          {t('table.labels.teamData', 'Dados da Equipe')}
        </Tag>
      ),
    },
    feedback: {
      tag: (
        <Tag color="bg-sky-700" className="py-1.5 sm:py-2 whitespace-nowrap">
          {t('evaluation.evaluation', 'Avaliação')}
        </Tag>
      ),
    },
    cards: {
      tag: (
        <Tag
          color="bg-emerald-700"
          className="py-1.5 sm:py-2 whitespace-nowrap"
        >
          Cards
        </Tag>
      ),
    },
    competition: {
      tag: (
        <Tag color="bg-red-700" className="py-1.5 sm:py-2 whitespace-nowrap">
          {t('competition.title', 'Competições')}
        </Tag>
      ),
    },
    profile: {
      tag: (
        <Tag color="bg-violet-700" className="py-1.5 sm:py-2 whitespace-nowrap">
          Meu Perfil
        </Tag>
      ),
    },
    'feedback-by-profile': {
      tag: (
        <Tag color="bg-sky-700" className="py-1.5 sm:py-2 whitespace-nowrap">
          {t('evaluation.evaluation', 'Avaliação')}
        </Tag>
      ),
    },
  }

  const currentStepTag = stepConfig[step]?.tag || stepConfig.home.tag

  // Check if we're on the home page
  const isHomePage = step === 'home'

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-16">
      <section className="text-white py-6 sm:py-12 md:py-20 pt-24 sm:pt-24 md:pt-32 pb-24 md:pb-20">
        {/* Header with unified navigation */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Title with Tag */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <h1 className="h2 text-3xl sm:text-4xl lg:text-5xl m-0 leading-tight">
              {t('generalTitle')}
            </h1>
            <div className="self-start sm:self-auto">{currentStepTag}</div>
          </div>

          {/* Desktop Navigation - Always visible when logged in */}
          <div className="w-full md:max-w-[60%] lg:max-w-[50%]">
            <div className="hidden md:block mb-4">
              <StepSelector variant="grid" />
            </div>
            <GroupSelect />
          </div>
        </div>

        {/* Content Area */}
        <div className={isHomePage ? '' : 'min-h-[50vh]'}>
          {step === 'home' && <DashboardHome onNavigate={handleNavigate} />}
          {step === 'table' && <DashboardTable />}
          {step === 'feedback' && <DashboardFeedback />}
          {step === 'feedback-by-profile' && profileUserUuid && (
            <DashboardFeedbackByProfile profileUserUuid={profileUserUuid} />
          )}
          {step === 'cards' && <DashboardCards user={user} />}
          {step === 'competition' && <DashboardCompetition />}
          {step === 'profile' && <DashboardProfile />}
        </div>
      </section>

      {/* Footer Section - Only show on non-home pages */}
      {!isHomePage && (
        <section className="text-white bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 flex items-center justify-center text-center mb-24 md:mb-20 border border-white/5">
          <div className="max-w-lg">
            <h2 className="text-xl sm:text-2xl md:text-3xl mb-2 sm:mb-3">
              {t('dashboard.footerTitle')}
            </h2>
            <p className="text-sm sm:text-base md:text-xl text-white/70">
              {t('dashboard.footerSubtitle')}
            </p>
          </div>
        </section>
      )}

      {/* Mobile Navigation - Always visible */}
      <div className="md:hidden">
        <StepSelector variant="mobile" />
      </div>
    </div>
  )
}

export default Dashboard
